import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../partage/api/client';
import { aujourdhui } from '../partage/datesEnMots';
import { cles } from '../partage/relectures';
import { useAuPouce, useCouche } from '../partage/surface';
import { useAngleDesGestes } from '../partage/useContexteDesGestes';
import {
  CALENDRIER,
  CHRONOLOGIE,
  datesChoisies,
  ENVOYER_DANS_MON_AGENDA,
  ENVOYER_DANS_MON_AGENDA_TITRE,
  RIEN_DE_DATE_ICI,
  TOUS,
} from '../partage/vocabulaire';
import { partiesDeLAgenda } from './agendaDansUnAngle';
import { AgendaAuPouce } from './AgendaAuPouce';
import { AgendaVide } from './AgendaVide';
import { Calendrier } from './Calendrier';
import { Chronologie, bulleDe, dossiersDe } from './Chronologie';
import { PreparerUnJour } from '../maintenant';
import { teinteDuProjet } from '../apparence';
import './Agenda.css';

/** Ce que l'export porte au plus d'un coup (la route l'accepte jusque-là). */
const EXPORT_MAX = 200;

/**
 * « Agenda » sur l'ordinateur (chantier `revue-design`, ticket 06, maquette `design/agenda.html`,
 * décisions d'Igor du 2026-10-03, `en-attente.md`, « L'Agenda »).
 *
 * En haut, « Agenda », le choix des vues et « ↓ Envoyer dans mon agenda ». **Le Calendrier
 * s'ouvre**, la Chronologie est à un clic. Le filtre par projet reste (à droite du mois dans le
 * Calendrier, au-dessus des semaines dans la Chronologie) ; la légende et le bloc explicatif du
 * bas sont partis, le bouton « Planifier ta journée » aussi : un clic sur un jour le remplace.
 *
 * L'habit est celui du carnet (chantier `agenda-papier`, maquette du carnet `agenda.html`) : les
 * vues et l'export sur un même rail, le mois en calendrier papier. Un jour cliqué ouvre « Préparer
 * ma journée » à droite du mois, sur une fiche scotchée (`PreparerUnJour.tsx`, habit `fiche`),
 * aujourd'hui compris ; sans jour ouvert, le mois prend toute la largeur. La vue, le mois et le jour ouvert vivent dans l'adresse (`?vue=…&mois=…&jour=…`) :
 * le lien « Ma journée » de « Maintenant » (`/agenda?jour=<aujourd'hui>`) ouvre le Calendrier
 * sur ce jour, le panneau ouvert ; un rechargement les garde ; le chemin reste `/agenda`, le seul
 * que le tuto regarde (`aide/ecranDuTuto.ts`).
 *
 * L'export porte la sélection faite dans la Chronologie ; sans sélection, tout ce qui est daté
 * dans les semaines montrées (filtre compris).
 *
 * Sans rien de daté nulle part, l'Agenda ouvert sans rien demander montre sa page de
 * présentation (`AgendaVide.tsx`) ; un jour demandé ouvre toujours le Calendrier.
 *
 * Au téléphone (`useAuPouce`), tout cela cède la place à `AgendaAuPouce.tsx` (ticket 10 de
 * telephone), qui ne change pas.
 *
 * **Dans l'angle Agenda d'une feuille** (chantier angles-du-projet, ticket 03), le même écran
 * avec `angle` : la portée est la bulle ouverte, filtrée par le serveur ; ce qui s'efface est dit
 * par `partiesDeLAgenda` (`agendaDansUnAngle.ts`). Une carte de la Chronologie ouvre sa fiche
 * comme la feuille le décide (sur place), pas sa feuille.
 */
export function Agenda({ angle }: { angle?: { portee: string; onOuvrir: (id: string) => void } } = {}) {
  const naviguer = useNavigate();
  const [parametres, setParametres] = useSearchParams();
  const parties = partiesDeLAgenda({ dansUnAngle: angle !== undefined });
  // L'angle des gestes faits ici (journal des gestes, 05) ; dans une feuille, c'est elle qui le dit.
  useAngleDesGestes(angle ? null : 'agenda');
  const agenda = useQuery({
    queryKey: angle ? cles.agendaDe(angle.portee) : cles.agenda,
    queryFn: () => api.agenda(angle?.portee),
  });
  // Dans un angle, la vue et le mois vivent ici : l'adresse reste celle de la feuille.
  const [dansLAngle, setDansLAngle] = useState<{
    vue: 'chronologie' | 'calendrier';
    mois: string | null;
  }>({
    vue: 'calendrier',
    mois: null,
  });
  const [dossierRetenu, setDossierRetenu] = useState<string | null>(null);
  const [choisis, setChoisis] = useState<Set<string>>(() => new Set());
  // Ce que le panneau dit en se refermant (« Ta journée du … est prête »), quelques secondes.
  const [annonce, setAnnonce] = useState<string | null>(null);
  const auPouce = useAuPouce();
  // Couché, l'Agenda du téléphone, le mois et le jour côte à côte (chantier telephone-couche-suite, 04).
  const couche = useCouche();
  useEffect(() => {
    if (!annonce) return;
    const minuterie = setTimeout(() => setAnnonce(null), 4000);
    return () => clearTimeout(minuterie);
  }, [annonce]);

  // Au téléphone, l'Agenda du téléphone, inchangé (ticket 10 de telephone). Une feuille n'y montre pas l'angle.
  if ((auPouce || couche) && !angle) return <AgendaAuPouce couche={couche} />;

  const demandee = parties.adresse ? parametres.get('vue') : dansLAngle.vue;
  const vue = demandee === 'chronologie' ? 'chronologie' : 'calendrier';
  const mois = parties.adresse ? parametres.get('mois') : dansLAngle.mois;
  const jourDemande = parties.preparer ? parametres.get('jour') : null;
  /*
   * Ouvert sans rien demander, l'Agenda montre « Préparer ma journée » sur aujourd'hui (retour
   * d'Igor du 2026-10-21 : « je veut que le planifier la journée sois ouvert de base sur la
   * journée du jours »). Refermée, la fiche le reste : l'adresse dit alors la vue.
   */
  const jour = jourDemande ?? (parties.preparer && parties.adresse && !demandee ? aujourdhui() : null);

  /** Change la vue, le mois ou le jour ouvert, sans empiler une page par geste. */
  function aller(suite: { vue?: 'chronologie' | 'calendrier'; mois?: string | null; jour?: string | null }) {
    if (!parties.adresse) {
      setDansLAngle((avant) => ({
        vue: suite.vue ?? avant.vue,
        mois: suite.mois === undefined ? avant.mois : suite.mois,
      }));
      return;
    }
    const p = new URLSearchParams();
    const laVue = suite.vue ?? vue;
    p.set('vue', laVue);
    if (laVue === 'calendrier') {
      const leMois = suite.mois === undefined ? mois : suite.mois;
      const leJour = suite.jour === undefined ? jour : suite.jour;
      if (leMois) p.set('mois', leMois);
      if (leJour) p.set('jour', leJour);
    }
    setParametres(p, { replace: true });
  }

  const classe = `agenda${angle ? ' agenda--angle' : ''}`;
  if (agenda.isPending) return <div className={classe} />;
  const toutes = agenda.data?.semaines ?? [];

  // Dans un angle, rien de daté dans la bulle : un mot, pas la présentation de tout l'Agenda.
  if (agenda.data?.vide && !parties.presentation) {
    return (
      <div className={classe}>
        <p className="agenda-rien-ici">{RIEN_DE_DATE_ICI}</p>
      </div>
    );
  }

  if (agenda.data?.vide && (vue === 'chronologie' || (!demandee && !jourDemande))) {
    return <AgendaVide onPlanifier={() => aller({ vue: 'calendrier' })} />;
  }

  /*
   * Les dossiers du filtre sortent des événements eux-mêmes : l'API de l'agenda ne renvoie
   * pas la liste des dossiers, et en inventer une demanderait une route. Un dossier sans
   * rien de daté n'a donc pas de pastille, ce qui est juste : il n'a rien à filtrer ici.
   */
  const dossiers = dossiersDe(toutes);
  const semaines = dossierRetenu
    ? toutes.map((s) => ({
        ...s,
        evenements: s.evenements.filter((e) => e.dossier?.id === dossierRetenu),
      }))
    : toutes;
  // La sélection ne vaut que là où elle se voit, dans la Chronologie.
  const aExporter =
    vue === 'chronologie' && choisis.size > 0
      ? [...choisis].map(bulleDe)
      : [...new Set(semaines.flatMap((s) => s.evenements.map((e) => e.bulleId)))];
  const lien = aExporter.length > 0 ? api.urlExport([...new Set(aExporter)].slice(0, EXPORT_MAX)) : undefined;

  function basculer(cle: string) {
    setChoisis((avant) => {
      const apres = new Set(avant);
      if (!apres.delete(cle)) apres.add(cle);
      return apres;
    });
  }

  const filtres = (
    <>
      <button
        type="button"
        className={dossierRetenu === null ? 'agenda-pastille agenda-pastille--tous' : 'agenda-pastille'}
        aria-pressed={dossierRetenu === null}
        onClick={() => setDossierRetenu(null)}
      >
        {TOUS}
      </button>
      {dossiers.map((d) => (
        <button
          key={d.id}
          type="button"
          className={dossierRetenu === d.id ? 'agenda-pastille agenda-pastille--retenue' : 'agenda-pastille'}
          aria-pressed={dossierRetenu === d.id}
          onClick={() => setDossierRetenu(dossierRetenu === d.id ? null : d.id)}
          style={{ ['--teinte' as string]: teinteDuProjet(d.couleur) }}
        >
          <span className="agenda-puce" aria-hidden="true" />
          {d.titre}
        </button>
      ))}
    </>
  );

  const panneau = vue === 'calendrier' && jour;

  return (
    <div className={classe}>
      <header className="agenda-tete agenda-tete--barre">
        {parties.titre && <h1>Agenda</h1>}
        {vue === 'chronologie' && choisis.size > 0 && <span className="agenda-choisies">{datesChoisies(choisis.size)}</span>}
        {/* Les vues et l'export sur un même rail, comme la maquette (`.reglages`). */}
        <div className="agenda-reglages">
          <div className="agenda-bascule" role="tablist" aria-label="Comment regarder l’Agenda">
            <button
              type="button"
              role="tab"
              aria-selected={vue === 'chronologie'}
              className={vue === 'chronologie' ? 'est-choisi' : ''}
              onClick={() => aller({ vue: 'chronologie' })}
            >
              {CHRONOLOGIE}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={vue === 'calendrier'}
              className={vue === 'calendrier' ? 'est-choisi' : ''}
              onClick={() => aller({ vue: 'calendrier' })}
            >
              {CALENDRIER}
            </button>
          </div>
          <div className="agenda-export" data-tuto="agenda-export">
            <a
              className={`agenda-envoyer${lien ? '' : ' est-eteint'}`}
              href={lien}
              aria-disabled={!lien}
              title={ENVOYER_DANS_MON_AGENDA_TITRE}
            >
              <span aria-hidden="true">↓</span> {ENVOYER_DANS_MON_AGENDA}
            </a>
          </div>
        </div>
      </header>

      {vue === 'chronologie' ? (
        <>
          {parties.filtres && <div className="agenda-filtres">{filtres}</div>}
          <Chronologie
            semaines={semaines}
            choisis={choisis}
            onBasculer={basculer}
            onOuvrir={angle ? angle.onOuvrir : (id) => naviguer(`/feuille/${id}`)}
            onOuvrirLaJournee={(j) => aller({ vue: 'calendrier', mois: j.slice(0, 7), jour: j })}
          />
        </>
      ) : (
        // Le mois à gauche ; un jour ouvert, sa fiche « Préparer ma journée » scotchée à droite.
        <div className={`agenda-grille${panneau ? '' : ' agenda-grille--seul'}`}>
          <Calendrier
            mois={mois ?? (jour ? jour.slice(0, 7) : null)}
            jour={jour}
            portee={angle?.portee ?? null}
            dossierRetenu={dossierRetenu}
            filtres={parties.filtres ? filtres : undefined}
            onMois={(m) => aller({ mois: m, jour: null })}
            onJour={parties.preparer ? (j) => aller({ jour: j }) : undefined}
          />
          {panneau && (
            <PreparerUnJour
              key={jour}
              jour={jour}
              habit="fiche"
              onFermer={(message) => {
                aller({ jour: null });
                if (message) setAnnonce(message);
              }}
            />
          )}
        </div>
      )}
      <p className={`agenda-annonce${annonce ? ' est-visible' : ''}`} role="status" aria-live="polite">
        {annonce}
      </p>
    </div>
  );
}
